/**
 * Rudrik — Full KYC + AML Journey Engine
 * ========================================
 * Multi-step wizard:
 *  Step 1 — Customer + KYC inputs
 *  Step 2 — AML inputs
 *  Step 3 — Synthesised final report + PDF certificate
 *
 * Two modes: AUTO (just name + PAN + DOB → orchestrate) | MANUAL (paste each).
 * Session-only — nothing is persisted to MongoDB.
 */
import React, { useMemo, useState } from "react";
import axios from "axios";
import {
  Workflow, ShieldCheck, AlertTriangle, Sparkles, FileDown, Loader2,
  ChevronRight, ChevronLeft, RefreshCw, CheckCircle2, XCircle, BadgeAlert,
  ScrollText, Wand2, ListChecks, FileJson,
} from "lucide-react";
import { toast } from "sonner";
import IdentityResultCard from "../components/IdentityResultCard";
import { fromKycJourney } from "../lib/identityNormalizers";

const API = `${window.location.origin}/api/journey`;

const DECISION_COLOR = {
  APPROVED: "#22c55e",
  APPROVED_WITH_CONDITIONS: "#84cc16",
  PENDING_DOCS: "#f59e0b",
  EDD_REQUIRED: "#f97316",
  REJECTED: "#dc2626",
  BLOCKED: "#991b1b",
};
const RISK_COLOR = { LOW: "#22c55e", MEDIUM: "#f59e0b", HIGH: "#f97316", CRITICAL: "#dc2626" };
const PILL_COLOR = (v) => {
  if (["PASS", "CLEAR", "CLEAN", "LIVE", "NONE", "SDD", "STANDARD"].includes(v)) return "#22c55e";
  if (["PENDING", "MEDIUM"].includes(v)) return "#f59e0b";
  if (["FAIL", "MATCH", "DETECTED", "CASES_FOUND", "SHELL_RISK", "STRIKE_OFF", "ACTION_FOUND", "NEGATIVE_FOUND", "DOMESTIC", "FOREIGN", "EDD", "BLOCK"].includes(v)) return "#dc2626";
  return "#94a3b8";
};

function Section({ title, icon: Icon, children, testId }) {
  return (
    <section data-testid={testId} style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 16, marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#94a3b8", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10 }}>
        {Icon && <Icon size={13} style={{ color: "#2563eb" }} />} {title}
      </div>
      {children}
    </section>
  );
}

function StatusPill({ label, value, testId }) {
  const c = PILL_COLOR(value);
  return (
    <div data-testid={testId} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 10px", background: "#080c12", border: `1px solid ${c}55`, borderRadius: 6 }}>
      <span style={{ color: "#cbd5e1", fontSize: 11, fontWeight: 600 }}>{label}</span>
      <span style={{ color: c, fontSize: 11, fontWeight: 800, letterSpacing: 0.4 }}>{value || "—"}</span>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testId, options }) {
  const isSelect = !!options;
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</span>
      {isSelect ? (
        <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
          style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: 6, padding: "8px 10px", color: "#f1f5f9", fontSize: 12 }}>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId}
          style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: 6, padding: "8px 10px", color: "#f1f5f9", fontSize: 12 }} />
      )}
    </label>
  );
}

function BoolToggle({ label, value, onChange, testId }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: "#080c12", border: "1px solid #1e2530", borderRadius: 6, cursor: "pointer" }}>
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={testId} />
      <span style={{ color: "#cbd5e1", fontSize: 11, fontWeight: 600 }}>{label}</span>
    </label>
  );
}

const STEPS = ["KYC Inputs", "AML Inputs", "Final Report"];

export default function JourneyPage() {
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState("AUTO");          // AUTO | MANUAL
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // shared
  const [customerName, setCustomerName] = useState("");
  const [customerType, setCustomerType] = useState("INDIVIDUAL");

  // AUTO mode shortcuts
  const [pan, setPan] = useState("");
  const [dob, setDob] = useState("");
  const [nationality, setNationality] = useState("IN");
  const [cin, setCin] = useState("");

  // MANUAL — KYC
  const [panStatus, setPanStatus] = useState("PASS");
  const [aadhaarStatus, setAadhaarStatus] = useState("PASS");
  const [faceMatched, setFaceMatched] = useState(true);
  const [faceConfidence, setFaceConfidence] = useState(92);
  const [deepfake, setDeepfake] = useState(false);
  const [liveness, setLiveness] = useState("LIVE");
  const [bankVerified, setBankVerified] = useState(true);
  const [bankNameMatch, setBankNameMatch] = useState(true);
  const [vkycCompleted, setVkycCompleted] = useState(true);
  const [vkycResult, setVkycResult] = useState("PASS");

  // MANUAL — AML
  const [isSanctioned, setIsSanctioned] = useState(false);
  const [pepStatus, setPepStatus] = useState("NONE");
  const [mcaStatus, setMcaStatus] = useState("CLEAN");
  const [shellScore, setShellScore] = useState(0);
  const [directors, setDirectors] = useState(0);
  const [totalCases, setTotalCases] = useState(0);
  const [pmlaCases, setPmlaCases] = useState(0);
  const [edAction, setEdAction] = useState(false);
  const [amArticles, setAmArticles] = useState(0);

  const manualPayload = useMemo(() => ({
    customer_name: customerName,
    customer_type: customerType,
    pan: { status: panStatus, risk_level: panStatus === "PASS" ? "LOW" : "MEDIUM" },
    aadhaar: { status: aadhaarStatus, risk_level: aadhaarStatus === "PASS" ? "LOW" : "MEDIUM" },
    face_match: { matched: faceMatched, confidence: Number(faceConfidence) || 0, deepfake_detected: deepfake, liveness },
    bank: { verified: bankVerified, name_match: bankNameMatch },
    video_kyc: { completed: vkycCompleted, result: vkycResult },
    sanctions: { is_sanctioned: isSanctioned, pep_status: pepStatus, risk_level: isSanctioned ? "CRITICAL" : pepStatus !== "NONE" ? "HIGH" : "LOW", matches: [] },
    mca21: { status: mcaStatus, shell_score: Number(shellScore) || 0, directors: Number(directors) || 0 },
    courts: { total_cases: Number(totalCases) || 0, pmla_cases: Number(pmlaCases) || 0 },
    ed_cbi: { action_found: edAction, detail: "" },
    adverse_media: { articles: Number(amArticles) || 0, risk: Number(amArticles) > 2 ? "HIGH" : "CLEAN" },
  }), [customerName, customerType, panStatus, aadhaarStatus, faceMatched, faceConfidence, deepfake, liveness, bankVerified, bankNameMatch, vkycCompleted, vkycResult, isSanctioned, pepStatus, mcaStatus, shellScore, directors, totalCases, pmlaCases, edAction, amArticles]);

  const run = async () => {
    if (!customerName.trim()) {
      toast.error("Customer name is required");
      setStep(0);
      return;
    }
    setLoading(true); setResult(null);
    try {
      const { data } = mode === "AUTO"
        ? await axios.post(`${API}/auto-run`, {
            customer_name: customerName.trim(),
            customer_type: customerType,
            pan: pan || undefined,
            dob: dob || undefined,
            nationality: nationality || "IN",
            cin: cin || undefined,
          }, { withCredentials: true, timeout: 45000 })
        : await axios.post(`${API}/synthesize`, manualPayload, { withCredentials: true, timeout: 45000 });

      setResult(data.result);
      setStep(2);
      const d = data.result?.final_decision;
      if (d === "APPROVED") toast.success(`APPROVED — score ${data.result?.scores?.final_score}/100`);
      else if (d === "BLOCKED" || d === "REJECTED") toast.error(`${d} — review required`);
      else toast.warning(`${d}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Journey synthesis failed");
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    if (!result) return;
    try {
      const res = await axios.post(`${API}/certificate.pdf`, { result }, {
        withCredentials: true, responseType: "blob", timeout: 30000,
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `rudrik-journey-${result.journey_id || "report"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Certificate downloaded");
    } catch (e) {
      toast.error("Failed to download PDF");
    }
  };

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(result, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rudrik-journey-${result.journey_id || "report"}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const reset = () => {
    setStep(0); setResult(null);
  };

  const finalScore = result?.scores?.final_score ?? 0;
  const decisionColor = DECISION_COLOR[result?.final_decision] || "#94a3b8";

  return (
    <div data-testid="journey-page" style={{ color: "#f1f5f9" }}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <Workflow style={{ width: 26, height: 26, color: "#2563eb" }} />
          <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>KYC + AML Journey Engine</h1>
          <span data-testid="session-badge" style={{ background: "rgba(148,163,184,0.12)", color: "#cbd5e1", border: "1px solid #94a3b855", fontSize: 10, padding: "3px 8px", borderRadius: 4, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>Session-only</span>
        </div>
      </div>
      <p style={{ color: "#94a3b8", fontSize: 13, marginBottom: 16 }}>
        Unified orchestrator across PAN, Aadhaar, Face Match, Bank, VKYC, Sanctions/PEP, MCA21, Courts, ED/CBI & Adverse Media — one decision, one certificate.
      </p>

      {/* Mode toggle */}
      <div style={{ display: "inline-flex", gap: 4, padding: 3, background: "#080c12", border: "1px solid #1e2530", borderRadius: 8, marginBottom: 18 }}>
        {["AUTO", "MANUAL"].map((m) => (
          <button key={m} onClick={() => setMode(m)} data-testid={`mode-${m.toLowerCase()}`}
            style={{ background: mode === m ? "linear-gradient(135deg, #2563eb, #1d4ed8)" : "transparent",
                     color: mode === m ? "white" : "#94a3b8", border: "none", borderRadius: 6,
                     padding: "7px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            {m === "AUTO" ? "Auto-Orchestrate" : "Manual Paste"}
          </button>
        ))}
      </div>

      {/* Stepper */}
      <div style={{ display: "flex", gap: 6, marginBottom: 18 }}>
        {STEPS.map((s, i) => (
          <div key={s} data-testid={`step-${i}`} style={{
            flex: 1, padding: "10px 12px", borderRadius: 8,
            background: step === i ? "linear-gradient(135deg, rgba(37,99,235,0.18), rgba(29,78,216,0.08))" : "#0d1117",
            border: `1px solid ${step === i ? "#2563eb" : "#1e2530"}`,
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <div style={{ width: 22, height: 22, borderRadius: "50%", background: step >= i ? "#2563eb" : "#1e2530",
                         color: "white", display: "flex", alignItems: "center", justifyContent: "center",
                         fontSize: 11, fontWeight: 700 }}>{i + 1}</div>
            <div style={{ color: step === i ? "#f1f5f9" : "#94a3b8", fontSize: 12, fontWeight: 700 }}>{s}</div>
          </div>
        ))}
      </div>

      {/* STEP 1 — KYC Inputs */}
      {step === 0 && (
        <Section title="Customer + KYC Inputs" icon={ShieldCheck} testId="step1-section">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
            <Field label="Customer name" value={customerName} onChange={setCustomerName} placeholder="Full legal name" testId="input-name" />
            <Field label="Customer type" value={customerType} onChange={setCustomerType} options={[{ value: "INDIVIDUAL", label: "Individual" }, { value: "COMPANY", label: "Company" }]} testId="input-type" />
            <Field label="Nationality (ISO-2)" value={nationality} onChange={setNationality} placeholder="IN" testId="input-nationality" />
          </div>

          {mode === "AUTO" ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="PAN" value={pan} onChange={(v) => setPan(v.toUpperCase())} placeholder="ABCDE1234F" testId="input-pan" />
              <Field label="Date of birth" value={dob} onChange={setDob} type="date" testId="input-dob" />
              {customerType === "COMPANY" && (
                <Field label="CIN (company)" value={cin} onChange={setCin} placeholder="U72200KA2020PTC..." testId="input-cin" />
              )}
            </div>
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 10 }}>
                <Field label="PAN status" value={panStatus} onChange={setPanStatus} options={[{ value: "PASS", label: "Pass" }, { value: "FAIL", label: "Fail" }, { value: "PENDING", label: "Pending" }]} testId="m-pan-status" />
                <Field label="Aadhaar status" value={aadhaarStatus} onChange={setAadhaarStatus} options={[{ value: "PASS", label: "Pass" }, { value: "FAIL", label: "Fail" }, { value: "PENDING", label: "Pending" }]} testId="m-aadhaar-status" />
                <Field label="Face confidence (0-100)" value={faceConfidence} onChange={setFaceConfidence} type="number" testId="m-face-conf" />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
                <BoolToggle label="Face matched" value={faceMatched} onChange={setFaceMatched} testId="m-face-matched" />
                <BoolToggle label="Deepfake detected" value={deepfake} onChange={setDeepfake} testId="m-deepfake" />
                <Field label="Liveness" value={liveness} onChange={setLiveness} options={[{ value: "LIVE", label: "Live" }, { value: "SPOOF", label: "Spoof" }, { value: "PENDING", label: "Pending" }]} testId="m-liveness" />
                <Field label="Video KYC result" value={vkycResult} onChange={setVkycResult} options={[{ value: "PASS", label: "Pass" }, { value: "FAIL", label: "Fail" }, { value: "PENDING", label: "Pending" }]} testId="m-vkyc-result" />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                <BoolToggle label="Bank verified" value={bankVerified} onChange={setBankVerified} testId="m-bank-verified" />
                <BoolToggle label="Bank name match" value={bankNameMatch} onChange={setBankNameMatch} testId="m-bank-name" />
                <BoolToggle label="VKYC completed" value={vkycCompleted} onChange={setVkycCompleted} testId="m-vkyc-completed" />
              </div>
            </>
          )}
        </Section>
      )}

      {/* STEP 2 — AML Inputs (manual only — for AUTO, just confirm + run) */}
      {step === 1 && (
        <Section title={mode === "AUTO" ? "AML Sources (orchestrated)" : "AML Inputs"} icon={BadgeAlert} testId="step2-section">
          {mode === "AUTO" ? (
            <div style={{ background: "#080c12", border: "1px dashed #1e2530", borderRadius: 8, padding: 14, color: "#cbd5e1", fontSize: 12, lineHeight: 1.6 }}>
              Rudrik will fan-out to <b>Sanctions/PEP (OpenSanctions live)</b>, <b>MCA21</b>, <b>Courts</b>, <b>ED/CBI</b> and <b>Adverse Media</b> in parallel using the customer name &amp; identifiers from Step 1, then synthesize the final decision via Claude Haiku 4.5.
              <div style={{ marginTop: 10, color: "#94a3b8", fontSize: 11 }}>
                For deeper control over any individual source, switch to <b>Manual Paste</b> mode.
              </div>
            </div>
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
                <BoolToggle label="Sanctions match" value={isSanctioned} onChange={setIsSanctioned} testId="m-sanctions" />
                <Field label="PEP status" value={pepStatus} onChange={setPepStatus} options={[{ value: "NONE", label: "None" }, { value: "DOMESTIC", label: "Domestic" }, { value: "FOREIGN", label: "Foreign" }, { value: "CRITICAL", label: "Critical" }]} testId="m-pep" />
                <Field label="MCA21" value={mcaStatus} onChange={setMcaStatus} options={[{ value: "CLEAN", label: "Clean" }, { value: "SHELL_RISK", label: "Shell Risk" }, { value: "STRIKE_OFF", label: "Strike Off" }]} testId="m-mca" />
                <Field label="Shell score (0-100)" value={shellScore} onChange={setShellScore} type="number" testId="m-shell" />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
                <Field label="Court cases (total)" value={totalCases} onChange={setTotalCases} type="number" testId="m-courts-total" />
                <Field label="PMLA cases" value={pmlaCases} onChange={setPmlaCases} type="number" testId="m-pmla" />
                <BoolToggle label="ED / CBI action" value={edAction} onChange={setEdAction} testId="m-ed" />
                <Field label="Adverse media articles" value={amArticles} onChange={setAmArticles} type="number" testId="m-am" />
              </div>
            </>
          )}
        </Section>
      )}

      {/* STEP 3 — Final Report */}
      {step === 2 && result && (
        <>
          {/* Unified identity card */}
          <div style={{ marginBottom: 14 }}>
            <IdentityResultCard person={fromKycJourney(result)} />
          </div>
          {/* Hero verdict */}
          <div data-testid="final-report" style={{
            background: "#0d1117", border: `1px solid ${decisionColor}66`, borderRadius: 12, padding: 22, marginBottom: 14,
            display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 1fr", gap: 16, alignItems: "center",
          }}>
            <div>
              <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>Final Decision</div>
              <div data-testid="final-decision" style={{ color: decisionColor, fontSize: 22, fontWeight: 800, marginTop: 4 }}>
                {result.final_decision}
              </div>
              <div style={{ color: "#cbd5e1", fontSize: 12, marginTop: 6 }}>{result.decision_reason}</div>
              <div style={{ color: "#64748b", fontSize: 10, marginTop: 8 }}>
                Journey {result.journey_id} · Certificate {result.certificate?.number}
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>Final Score</div>
              <div data-testid="final-score" style={{ color: decisionColor, fontSize: 38, fontWeight: 800 }}>{finalScore}</div>
              <div style={{ color: "#64748b", fontSize: 10 }}>{result.scores?.score_formula}</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>KYC / AML</div>
              <div style={{ color: "#f1f5f9", fontSize: 18, fontWeight: 800, marginTop: 4 }}>
                <span style={{ color: PILL_COLOR(result.kyc_summary?.kyc_complete ? "PASS" : "PENDING") }}>{result.scores?.kyc_score}</span>
                <span style={{ color: "#475569" }}> / </span>
                <span style={{ color: PILL_COLOR(result.aml_summary?.aml_complete ? "CLEAR" : "PENDING") }}>{result.scores?.aml_score}</span>
              </div>
              <div style={{ color: "#64748b", fontSize: 10, marginTop: 4 }}>{result.cdd_tier} · {result.overall_risk}</div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <button onClick={downloadPdf} data-testid="download-pdf"
                style={{ background: "linear-gradient(135deg, #2563eb, #1d4ed8)", color: "white", border: "none",
                         borderRadius: 6, padding: "10px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer",
                         display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                <FileDown size={13} /> PDF Certificate
              </button>
              <button onClick={downloadJson} data-testid="download-json"
                style={{ background: "transparent", color: "#94a3b8", border: "1px solid #1e2530",
                         borderRadius: 6, padding: "8px 12px", fontSize: 11, fontWeight: 700, cursor: "pointer",
                         display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                <FileJson size={12} /> JSON
              </button>
              <button onClick={reset} data-testid="new-journey"
                style={{ background: "transparent", color: "#94a3b8", border: "1px solid #1e2530",
                         borderRadius: 6, padding: "8px 12px", fontSize: 11, fontWeight: 700, cursor: "pointer",
                         display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                <RefreshCw size={11} /> New Journey
              </button>
            </div>
          </div>

          {/* Executive summary */}
          <Section title="Executive Summary" icon={Sparkles} testId="exec-summary">
            <div style={{ color: "#e2e8f0", fontSize: 13, lineHeight: 1.6 }}>{result.executive_summary}</div>
          </Section>

          {/* KYC + AML side-by-side */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <Section title="KYC Summary" icon={CheckCircle2} testId="kyc-summary">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                <StatusPill label="PAN" value={result.kyc_summary?.pan} testId="kyc-pan" />
                <StatusPill label="Aadhaar" value={result.kyc_summary?.aadhaar} testId="kyc-aadhaar" />
                <StatusPill label="Face match" value={result.kyc_summary?.face_match} testId="kyc-face" />
                <StatusPill label="Liveness" value={result.kyc_summary?.liveness} testId="kyc-liveness" />
                <StatusPill label="Deepfake" value={result.kyc_summary?.deepfake} testId="kyc-deepfake" />
                <StatusPill label="Bank" value={result.kyc_summary?.bank} testId="kyc-bank" />
                <StatusPill label="Video KYC" value={result.kyc_summary?.video_kyc} testId="kyc-vkyc" />
                <StatusPill label="Complete" value={result.kyc_summary?.kyc_complete ? "PASS" : "PENDING"} testId="kyc-complete" />
              </div>
              {result.kyc_summary?.kyc_missing?.length > 0 && (
                <div style={{ marginTop: 8, color: "#fbbf24", fontSize: 11 }}>
                  Missing: {result.kyc_summary.kyc_missing.join(", ")}
                </div>
              )}
            </Section>

            <Section title="AML Summary" icon={ShieldCheck} testId="aml-summary">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                <StatusPill label="Sanctions" value={result.aml_summary?.sanctions} testId="aml-sanctions" />
                <StatusPill label="PEP" value={result.aml_summary?.pep} testId="aml-pep" />
                <StatusPill label="Courts" value={result.aml_summary?.courts} testId="aml-courts" />
                <StatusPill label="MCA21" value={result.aml_summary?.mca21} testId="aml-mca21" />
                <StatusPill label="ED / CBI" value={result.aml_summary?.ed_cbi} testId="aml-ed" />
                <StatusPill label="Adverse media" value={result.aml_summary?.adverse_media} testId="aml-am" />
              </div>
            </Section>
          </div>

          {/* Flags */}
          {result.risk_flags?.length > 0 && (
            <Section title="Risk Flags" icon={AlertTriangle} testId="risk-flags-section">
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {result.risk_flags.map((f, i) => {
                  const c = RISK_COLOR[(f.severity || "MEDIUM").toUpperCase()] || "#f59e0b";
                  return (
                    <div key={`${f.flag}-${i}`} data-testid={`risk-flag-${i}`}
                         style={{ background: `${c}10`, border: `1px solid ${c}55`, borderRadius: 6, padding: "8px 12px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <AlertTriangle size={12} style={{ color: c }} />
                        <span style={{ color: c, fontSize: 12, fontWeight: 700 }}>{f.flag}</span>
                        <span style={{ marginLeft: "auto", color: c, fontSize: 9, fontWeight: 700, textTransform: "uppercase" }}>{f.severity} · {f.source}</span>
                      </div>
                      {f.action && <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 3, marginLeft: 20 }}>→ {f.action}</div>}
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          {/* Emergent */}
          {result.emergent_risks?.length > 0 && (
            <Section title="Emergent Cross-Source Risks" icon={Wand2} testId="emergent-section">
              {result.emergent_risks.map((er, i) => (
                <div key={i} data-testid={`emergent-${i}`} style={{ background: "#080c12", border: "1px solid #a855f755", borderRadius: 6, padding: 10, marginBottom: 6 }}>
                  <div style={{ color: "#c084fc", fontSize: 12, fontWeight: 700 }}>{er.pattern} <span style={{ color: "#94a3b8", fontSize: 10 }}>({(er.sources || []).join(" + ")})</span></div>
                  <div style={{ color: "#cbd5e1", fontSize: 11, marginTop: 3 }}>{er.finding}</div>
                </div>
              ))}
            </Section>
          )}

          {/* STR + Monitoring */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <Section title="STR Filing" icon={ScrollText} testId="str-section">
              {result.str_required ? (
                <>
                  <div style={{ color: "#fca5a5", fontSize: 13, fontWeight: 700 }}>STR Required</div>
                  <div style={{ color: "#cbd5e1", fontSize: 11, marginTop: 4 }}>{result.str_reason}</div>
                  <div style={{ color: "#f59e0b", fontSize: 11, marginTop: 6 }}>Deadline: {result.str_deadline}</div>
                </>
              ) : (
                <div style={{ color: "#22c55e", fontSize: 12, fontWeight: 700 }}>No STR required</div>
              )}
            </Section>
            <Section title="Monitoring Plan" icon={ListChecks} testId="monitoring-section">
              <div style={{ color: "#cbd5e1", fontSize: 12 }}>
                Required: <b style={{ color: result.monitoring?.required ? "#22c55e" : "#94a3b8" }}>{result.monitoring?.required ? "Yes" : "No"}</b>
              </div>
              <div style={{ color: "#cbd5e1", fontSize: 12 }}>Frequency: <b>{result.monitoring?.frequency}</b></div>
              <div style={{ color: "#cbd5e1", fontSize: 12 }}>Next review: <b>{result.monitoring?.next_review}</b></div>
              {result.monitoring?.auto_alerts?.length > 0 && (
                <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 6 }}>Auto-alerts: {result.monitoring.auto_alerts.join(" · ")}</div>
              )}
            </Section>
          </div>

          {result.what_rudrik_found_that_hyperverge_cannot?.length > 0 && (
            <Section title="What Rudrik found that competitors miss" icon={Sparkles} testId="rudrik-edge">
              <ul style={{ color: "#cbd5e1", fontSize: 12, paddingLeft: 18, margin: 0, lineHeight: 1.7 }}>
                {result.what_rudrik_found_that_hyperverge_cannot.map((x, i) => <li key={i}>{x}</li>)}
              </ul>
            </Section>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", color: "#64748b", fontSize: 10, marginTop: 8 }}>
            <span>{result.ai_mode} · Cost ₹{result.total_cost_inr?.toFixed?.(2) ?? result.total_cost_inr} · {result.journey_time_seconds}s</span>
            <span>{result.certificate?.statement?.slice(0, 90)}…</span>
          </div>
        </>
      )}

      {/* Wizard nav */}
      {step !== 2 && (
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 18 }}>
          <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
            data-testid="back-btn"
            style={{ background: "transparent", border: "1px solid #1e2530", color: step === 0 ? "#475569" : "#94a3b8",
                     borderRadius: 6, padding: "9px 16px", fontSize: 12, fontWeight: 700,
                     cursor: step === 0 ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
            <ChevronLeft size={13} /> Back
          </button>
          {step < 1 ? (
            <button onClick={() => setStep((s) => s + 1)} data-testid="next-btn"
              style={{ background: "linear-gradient(135deg, #2563eb, #1d4ed8)", border: "none", color: "white",
                       borderRadius: 6, padding: "9px 18px", fontSize: 12, fontWeight: 700, cursor: "pointer",
                       display: "inline-flex", alignItems: "center", gap: 6 }}>
              Next <ChevronRight size={13} />
            </button>
          ) : (
            <button onClick={run} disabled={loading} data-testid="run-btn"
              style={{ background: loading ? "#1e2530" : "linear-gradient(135deg, #2563eb, #1d4ed8)",
                       border: "none", color: "white", borderRadius: 6, padding: "9px 18px",
                       fontSize: 12, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer",
                       display: "inline-flex", alignItems: "center", gap: 6 }}>
              {loading ? <Loader2 className="animate-spin" size={13} /> : <ShieldCheck size={13} />}
              {loading ? "Synthesizing…" : "Run Journey"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
